import { makeStyles } from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import UploadIcon from "../../../images/material-symbols_upload.png";
import UpdateLogoIcon from "../../../images/update-logo-icon.png";
import DeleteLogoIcon from "../../../images/delete-logo-icon.png";
import theme from "../../../themes";

const useStyles = makeStyles(() => ({
  uploadContainer: {
    width: "100%",
    maxWidth: "404px",
    height: "127px",
    background: theme.palette.background.paper,
    border: `1px dashed ${theme.palette.grey[500]}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    gap: "10px",
    cursor: "pointer",
    boxSizing: "border-box",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  uploadIcon: {
    width: "32px",
    height: "32px",
  },
  uploadText: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 400,
    fontSize: "18px",
    color: theme.palette.text.secondary,
    textAlign: "center",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
  logoPreviewContainer: {
    width: "100%",
    maxWidth: "404px",
    minHeight: "150px",
    background: theme.palette.background.paper,
    border: `1px dashed ${theme.palette.grey[500]}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "20px",
    gap: "24px",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      padding: "14px",
      gap: "14px",
      minHeight: 0,
    },
  },
  logoImageContainer: {
    width: "100px",
    height: "100px",
    borderRadius: "5px",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[300]}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: "72px",
      height: "72px",
    },
  },
  logoImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  actionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      gap: "8px",
    },
  },
  updateButton: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    cursor: "pointer",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "16px",
    fontWeight: 500,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.grey[600],
    border: "none",
    borderRadius: "8px",
    padding: "12px 24px",
    minWidth: "180px",
    "&:hover": {
      backgroundColor: theme.palette.grey[700],
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      padding: "8px 12px",
      minWidth: 0,
      width: "100%",
      gap: "6px",
    },
  },
  deleteButton: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    cursor: "pointer",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "16px",
    fontWeight: 500,
    color: theme.palette.error.contrastText,
    backgroundColor: theme.palette.error.main,
    border: "none",
    borderRadius: "8px",
    padding: "12px 24px",
    minWidth: "180px",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      padding: "8px 12px",
      minWidth: 0,
      width: "100%",
      gap: "6px",
    },
  },
  buttonIcon: {
    fontSize: "20px",
    color: theme.palette.primary.contrastText,
  },
  buttonIconImg: {
    width: "20px",
    height: "20px",
    [theme.breakpoints.down("sm")]: {
      width: "16px",
      height: "16px",
    },
  },
  deleteButtonIconImg: {
    width: "20px",
    height: "20px",
    filter: "brightness(0) invert(1)",
    [theme.breakpoints.down("sm")]: {
      width: "16px",
      height: "16px",
    },
  },
}));

const LogoPicker = ({
  logo,
  deleteLogo,
  base64,
  label,
}: {
  logo: any;
  deleteLogo: any;
  base64: any;
  label?: string;
}) => {
  const classes = useStyles();
  const uniqueId = `contained-button-file-${uuidv4()}`;

  return (
    <div>
      <input
        id={uniqueId}
        type="file"
        accept="image/*, png, jpeg, jpg"
        style={{ display: "none" }}
        name={`logo-${uuidv4()}`}
        onChange={base64}
      />

      {!logo ? (
        <label htmlFor={uniqueId} className={classes.uploadContainer}>
          <img src={UploadIcon} alt="Upload" className={classes.uploadIcon} />
          <p className={classes.uploadText}>Click to Upload</p>
        </label>
      ) : (
        <div className={classes.logoPreviewContainer}>
          <div className={classes.logoImageContainer}>
            <img src={logo} alt="Logo" className={classes.logoImage} />
          </div>
          <div className={classes.actionsContainer}>
            <label htmlFor={uniqueId} className={classes.updateButton}>
              <img src={UpdateLogoIcon} alt="Update" className={classes.buttonIconImg} />
              <span>Update Logo</span>
            </label>
            <div className={classes.deleteButton} onClick={deleteLogo}>
              <img src={DeleteLogoIcon} alt="Delete" className={classes.deleteButtonIconImg} />
              <span>Delete Logo</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoPicker;
